if (!customElements.get('cookie-banner')) {
  customElements.define(
    'cookie-banner',
    class CookieBanner extends ModalElement {
      constructor() {
        super();

        this._eventsBound = false;
        this._fallbackTimer = null;

        // Cookie banner should initialize immediately.
        // Do not use initWhenVisible because the banner starts hidden.
        this.init();
      }

      get testMode() {
        return this.getAttribute('data-test-mode') === 'true';
      }

      get delay() {
        return this.hasAttribute('data-delay')
          ? parseInt(this.getAttribute('data-delay'), 10)
          : 5;
      }

      get statusCookieName() {
        return 'munbyn_cookie_banner_status';
      }

      // Accept / Decline: do not show again for 180 days.
      get consentCookieDays() {
        return 180;
      }

      // Close X: only hide temporarily, because close is not consent.
      get closeCookieDays() {
        return 7;
      }

      init() {
        if (this.initialized) return;
        this.initialized = true;

        this.bindEvents();

        if (Shopify.designMode) return;

        if (this.hasSavedBannerStatus()) {
          return;
        }

        if (this.testMode) {
          this.load(this.delay);
          return;
        }

        this.loadConsentLibrary();

        // Fallback: if Shopify Consent API is not ready, still show the custom banner.
        this._fallbackTimer = window.setTimeout(() => {
          if (!window.Shopify?.customerPrivacy && !this.hasSavedBannerStatus()) {
            this.load(this.delay);
          }
        }, 2500);
      }

      bindEvents() {
        if (this._eventsBound) return;
        this._eventsBound = true;

        this.addEventListener('click', (event) => {
          const button = event.target.closest('button');
          if (!button) return;

          if (button.name === 'accept') {
            event.preventDefault();
            this.acceptPolicy();
          }

          if (button.name === 'decline') {
            event.preventDefault();
            this.declinePolicy();
          }

          if (button.name === 'close') {
            event.preventDefault();
            event.stopPropagation();
            this.closeBanner();
          }
        });
      }

      loadConsentLibrary() {
        if (!window.Shopify?.loadFeatures) {
          this.load(this.delay);
          return;
        }

        window.Shopify.loadFeatures([
          {
            name: 'consent-tracking-api',
            version: '0.1',
            onLoad: this.onConsentLibraryLoaded.bind(this)
          }
        ]);
      }

      load(delay) {
        if (Shopify.designMode) return;

        if (this.hasSavedBannerStatus()) {
          return;
        }

        window.setTimeout(() => {
          if (!this.hasSavedBannerStatus()) {
            this.forceVisible();
          }
        }, delay * 1000);
      }

      forceVisible() {
        this.hidden = false;
        this.removeAttribute('hidden');
        this.style.display = '';
        this.setAttribute('active', '');
        document.body.classList.add('has-cookie-banner');

        try {
          this.show();
        } catch (error) {
          // Fallback if theme modal show fails.
        }
      }

      forceHide() {
        try {
          this.hide();
        } catch (error) {
          // Fallback if theme modal hide fails.
        }

        this.hidden = true;
        this.setAttribute('hidden', '');
        this.removeAttribute('active');
        this.style.display = 'none';
        document.body.classList.remove('has-cookie-banner');
      }

      acceptPolicy() {
        this.setBannerStatus('accepted', this.consentCookieDays);
        this.forceHide();

        if (window.Shopify?.customerPrivacy?.setTrackingConsent) {
          window.Shopify.customerPrivacy.setTrackingConsent(true, this.noop);
        }

        document.dispatchEvent(
          new CustomEvent('munbyn:cookie-consent', {
            detail: {
              consent: true
            }
          })
        );
      }

      declinePolicy() {
        this.setBannerStatus('declined', this.consentCookieDays);
        this.forceHide();

        if (window.Shopify?.customerPrivacy?.setTrackingConsent) {
          window.Shopify.customerPrivacy.setTrackingConsent(false, this.noop);
        }

        document.dispatchEvent(
          new CustomEvent('munbyn:cookie-consent', {
            detail: {
              consent: false
            }
          })
        );
      }

      closeBanner() {
        // Close does not mean consent.
        // It only suppresses the banner for a shorter period.
        this.setBannerStatus('closed', this.closeCookieDays);
        this.forceHide();

        document.dispatchEvent(
          new CustomEvent('munbyn:cookie-banner-closed', {
            detail: {
              status: 'closed'
            }
          })
        );
      }

      onConsentLibraryLoaded(error) {
        if (this._fallbackTimer) {
          window.clearTimeout(this._fallbackTimer);
          this._fallbackTimer = null;
        }

        if (this.hasSavedBannerStatus()) {
          return;
        }

        if (error || !window.Shopify?.customerPrivacy) {
          this.load(this.delay);
          return;
        }

        const userTrackingConsent = window.Shopify.customerPrivacy.getTrackingConsent();

        if (userTrackingConsent === 'no_interaction') {
          this.load(this.delay);
        } else {
          // Shopify already has a consent decision, so save a local marker too.
          this.setBannerStatus('shopify_consent_exists', this.consentCookieDays);
        }
      }

      hasSavedBannerStatus() {
        return Boolean(this.getCookie(this.statusCookieName));
      }

      setBannerStatus(status, days) {
        this.setCookie(this.statusCookieName, status, days);
      }

      setCookie(name, value, days) {
        const maxAge = Math.max(1, days) * 24 * 60 * 60;
        const encodedName = encodeURIComponent(name);
        const encodedValue = encodeURIComponent(value);

        document.cookie = `${encodedName}=${encodedValue}; Max-Age=${maxAge}; Path=/; SameSite=Lax; Secure`;
      }

      getCookie(name) {
        const encodedName = encodeURIComponent(name);
        const cookies = document.cookie ? document.cookie.split('; ') : [];

        for (const cookie of cookies) {
          const [key, ...rest] = cookie.split('=');

          if (key === encodedName) {
            return decodeURIComponent(rest.join('='));
          }
        }

        return '';
      }

      noop() {}

      afterHide() {
        super.afterHide();
        document.body.classList.remove('has-cookie-banner');
      }

      afterShow() {
        super.afterShow();
        document.body.classList.add('has-cookie-banner');
      }
    }
  );
}